/**
 * Which projection of the table this device gets: the saloon or the top-down felt.
 *
 * ============================== THE RULE (Phase 7) ==============================
 * The saloon view is desktop only. It stands the cast round a tilted table in a
 * 1600 x 900 stage and it needs the width: on a frame narrower than 1024 px, or one
 * that is not at least 1.3 times as wide as it is tall, it is never offered — phones,
 * phones on their side (844 wide) and portrait tablets always get the top-down table.
 *
 * On a frame that fits, the player's own choice wins, and a player who has never
 * chosen gets the saloon. The choice is kept on the device (`prefs.view`), because a
 * laptop and a phone are different screens and the answer on one says nothing about
 * the other.
 *
 * "Frame" is the WINDOW, read with `matchMedia` and followed as it changes: a desktop
 * window dragged narrow drops to the top-down table and comes back when it is widened.
 * (`VITE_TABLE_VIEW=classic`, which selected the old felt above all of this, was retired
 * with it in Phase 9.)
 * ================================================================================
 */
import { useEffect, useState } from "react";
import { getPrefs, setPref, onPrefsChange, type TableView } from "./prefs";

export const SALOON_MIN_WIDTH = 1024;
export const SALOON_MIN_ASPECT = 1.3;

/** Does a frame this size get the saloon at all? */
export function saloonFits(width: number, height: number): boolean {
  return width >= SALOON_MIN_WIDTH && height > 0 && width / height >= SALOON_MIN_ASPECT;
}

/** The projection to draw: the choice where the saloon fits, the top-down table where it does not. */
export function effectiveView(choice: TableView | null, fits: boolean): TableView {
  if (!fits) return "topdown";
  return choice ?? "saloon";
}

/* The same rule as a media query, so the browser tells us when it flips. */
const QUERY = `(min-width: ${SALOON_MIN_WIDTH}px) and (min-aspect-ratio: 13/10)`;

/*
 * The window, through `globalThis` and typed by hand: this module is imported by the
 * Node tests for the rule above, where there is no DOM library and no `window`.
 */
interface Mq { matches: boolean; addEventListener(t: "change", f: () => void): void; removeEventListener(t: "change", f: () => void): void }
const win = globalThis as unknown as { matchMedia?: (q: string) => Mq; innerWidth?: number; innerHeight?: number };

function fitsNow(): boolean {
  if (win.matchMedia) return win.matchMedia(QUERY).matches;
  return win.innerWidth !== undefined && win.innerHeight !== undefined && saloonFits(win.innerWidth, win.innerHeight);
}

/** The view, whether the saloon is available here, and the setter the toggle uses. */
export function useTableView(): { view: TableView; fits: boolean; choice: TableView | null; set: (v: TableView) => void } {
  const [fits, setFits] = useState(fitsNow);
  const [choice, setChoice] = useState<TableView | null>(() => getPrefs().view);
  useEffect(() => {
    const mq = win.matchMedia?.(QUERY);
    if (!mq) return;
    const on = () => setFits(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  useEffect(() => onPrefsChange(() => setChoice(getPrefs().view)), []);
  return { view: effectiveView(choice, fits), fits, choice, set: (v) => setPref("view", v) };
}
